import { getArtistPerson } from '../getArtistPerson/getArtistPerson';
import { getDisplayYear } from '../getDisplayYear/getDisplayYear';
import { getJsonLdImages } from '../getJsonLdImages/getJsonLdImages';
import { getProductOffer } from '../getProductOffer/getProductOffer';
import { HENI_ORGANIZATION_REF } from '#lib/features/seo/constants/heni-organization.js';
import { OfferAvailability } from '#lib/features/seo/constants/offer-availability.js';
import type { EditionsArtwork } from '#lib/websites/editions/api/getEditionsArtworkPage.js';
import { omitEmpty } from '$global/utils/omitEmpty/omitEmpty';
import { stripMarkdown } from '$global/utils/stripMarkdown/stripMarkdown';

interface EditionJsonLdOptions {
	url: string;
	// The PDP shows the price only while the drop is live.
	isLive: boolean;
}

// Free-typed CMS strings: parseFloat would read "30 x 40" or "1,200" as a size.
const CENTIMETRES = /^\s*(\d+(?:\.\d+)?)\s*(?:cm)?\s*$/i;

const toCentimetres = (value: string | null | undefined) => {
	const centimetres = Number(CENTIMETRES.exec(value ?? '')?.[1]);
	return centimetres > 0
		? { '@type': 'QuantitativeValue', value: centimetres, unitCode: 'CMT' }
		: undefined;
};

export const getEditionJsonLd = (
	edition: EditionsArtwork,
	{ url, isLive }: EditionJsonLdOptions
) => {
	const offers = isLive
		? getProductOffer({
				price: edition.product?.price,
				currency: edition.product?.currency,
				availability: OfferAvailability.InStock,
				url,
			})
		: undefined;

	// Google flags a Product without offers as invalid.
	return omitEmpty({
		'@context': 'https://schema.org',
		'@type': offers ? ['Product', 'VisualArtwork'] : 'VisualArtwork',
		'@id': `${url}#product`,
		name: edition.name?.replaceAll('<br>', ', '),
		url,
		description: stripMarkdown(
			edition.short_description || edition.long_description || ''
		).trim(),
		image: getJsonLdImages([
			edition.image,
			...(edition.gallery_of_images ?? []).map(
				(junction) => junction.directus_files_id
			),
		]),
		brand: offers ? HENI_ORGANIZATION_REF : undefined,
		creator: (edition.artists ?? [])
			.map((junction) => getArtistPerson(junction.Artists_id))
			.filter(Boolean),
		dateCreated: getDisplayYear(edition.artwork_creation_date),
		artMedium: edition.medium,
		artEdition: edition.edition_size_description,
		// Same choice as the Dimensions row.
		size: edition.dimensions_description,
		width: edition.dimensions_description
			? undefined
			: toCentimetres(edition.width_cm),
		height: edition.dimensions_description
			? undefined
			: toCentimetres(edition.length_cm),
		offers,
	});
};
